import React from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import CustomEquipoWithTooltip from '@/DesignSystem/components/partidos/CustomEquipoWithTooltip';
import CustomImageComponent from '@/components/shared/custom/custom-image';
import CustomSectionWrapper from '@/DesignSystem/wrappers/CustomSectionWrapper';
import { getTeamImageById } from '@/helpers/images-helper';
import { getCompetitionImage } from '@/helpers/leagues-helper';
import { Rutas } from '@/constants/rutas';
import { translate } from '@/data/fixture-transalations';
import { getDateMMddHHmm } from '@/helpers/datetime-helper';
import HomeIcon from '@mui/icons-material/Home';
import FlightTakeoffIcon from '@mui/icons-material/FlightTakeoff';
import Link from 'next/link';

const ProximoPartidoCard = ({ fixture, equipoId, isLast }) => {
    const theme = useTheme();
    const isHome = fixture.home.id === equipoId;
    const rivalTeam = isHome ? fixture.away : fixture.home;
    const rivalImage = getTeamImageById(rivalTeam.id, 20);
    const competitionImage = getCompetitionImage(fixture.league);

    return (
        <Box
            component={Link}
            href={`${Rutas.PARTIDO}/${fixture.id}/alineaciones-probables`}
            sx={{
                flex: 1,
                minWidth: 0,
                height: 100,
                px: 0.5,
                py: 1,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'space-between',
                borderRight: isLast ? 'none' : `1px solid ${theme.palette.divider}`,
                textDecoration: 'none',
                backgroundColor: theme.palette.background.default,
                transition: 'background-color 0.2s',
                '&:hover': {
                    backgroundColor: theme.palette.action.hover,
                },
            }}
        >
            {/* Top: Competición y local/visitante */}
            <Box display="flex" alignItems="center" justifyContent="center" width="100%">
                <Box display="flex" alignItems="center" gap={0.5} px={0.5}>
                    <CustomImageComponent
                        src={competitionImage}
                        alt={fixture.league}
                        width={12}
                        height={12}
                    />
                    <Typography fontSize={12} color="text.secondary">
                        {translate(fixture.round, true)}
                    </Typography>
                </Box>
                {isHome ? (
                    <HomeIcon sx={{ fontSize: 14, color: theme.palette.text.primary }} />
                ) : (
                    <FlightTakeoffIcon sx={{ fontSize: 14, color: theme.palette.text.primary }} />
                )}
            </Box>

            {/* Middle: Escudo y nombre */}
            <CustomEquipoWithTooltip
                teamId={rivalTeam.id}
                src={rivalImage}
                alt={rivalTeam.name}
                width={24}
                height={24}
            />
            <Typography
                fontSize={12}
                textAlign="center"
                fontWeight={500}
                sx={{
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    width: '100%',
                }}
                title={rivalTeam.name}
            >
                {rivalTeam.name}
            </Typography>

            {/* Bottom: Fecha */}
            <Typography fontSize={11} color="text.secondary" textAlign="center" noWrap>
                {getDateMMddHHmm(fixture.date)}
            </Typography>
        </Box>
    );
};

const ProximosPartidosEquipo = ({ equipo }) => {
    const theme = useTheme();
    const proximosPartidos = equipo?.next ?? [];

    if (!proximosPartidos.length) return null;

    const equipoImage = getTeamImageById(equipo.id, 20);
    const maxPartidos = Math.min(proximosPartidos.length, 5);

    // Título compuesto con escudo + nombre + texto
    const customTitle = (
        <Box display="flex" alignItems="center" gap={1}>
            <CustomEquipoWithTooltip
                teamId={equipo.id}
                src={equipoImage}
                alt={equipo.name}
                width={20}
                height={20}
            />
            <Typography component="span" fontSize={14} fontWeight={500}>
                {equipo.name}
            </Typography>
            <Typography component="span" fontSize={14}>
                -
            </Typography>
            <Typography component="span" fontSize={14}>
                Próximos partidos
            </Typography>
        </Box>
    );

    return (
        <CustomSectionWrapper title={customTitle} color={theme.palette.primary.main} mb={2}>
            {/* Partidos */}
            <Box display="flex" width="100%">
                {proximosPartidos.slice(0, maxPartidos).map((fixture, index) => (
                    <ProximoPartidoCard
                        key={`${fixture.id}-${index}`}
                        fixture={fixture}
                        equipoId={equipo.id}
                        isLast={index === maxPartidos - 1}
                    />
                ))}
            </Box>
        </CustomSectionWrapper>
    );
};

export default ProximosPartidosEquipo;
